import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

import { shortlist, isShortlisted, unshortlist } from 'redux/modules/portfolio';

import Pro from 'components/Badges/Pro/Pro';
import Verified from 'components/Badges/Verified/Verified2';
import Icon from 'components/Icon/Icon';

import Rating from 'components/Rating/Rating';
import Shiitake from 'components/Shiitake/Shiitake';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import { skillLevels } from 'constants/constants';
import Markdown from 'components/Shared/Markdown';

import {
  calculateUserRating,
  get,
  isCreativeJob,
  isMusicJob,
} from 'helpers/HelperFunctions';

import Button from 'react-bootstrap/lib/Button';
import Dropdown from 'react-bootstrap/lib/Dropdown';
import MenuItem from 'react-bootstrap/lib/MenuItem';

import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';
import { isEqual } from 'lodash';
import { loadCredits } from 'redux/modules/portfolio';
import { trackEvent } from 'helpers/Analytics';
import AudioThumbnails from './AudioThumbnails/AudioThumbnails';
import ButtonOutlined from '../Buttons/Outlined/ButtonOutlined';
import LastSeen from '../LastSeen';
import ProjectCarousel from './ProjectCarousel/ProjectCarousel';
import styles from './UserCard2.module.scss';
import ButtonWithLoader from 'components/ButtonWithLoader/ButtonWithLoader';

@connect(
  ({
    auth: { user: authUser },
    entities: { credits: creditEntities, projects: projectEntities },
    portfolio,
  }) => ({ authUser, creditEntities, projectEntities, portfolio }),
  { loadCredits, shortlist, isShortlisted, unshortlist },
  null,
  { forwardRef: true }
)
export default class UserCard2 extends Component {
  static propTypes = {
    authUser: PropTypes.object,
    preferredRole: PropTypes.string,
    collaborations: PropTypes.array,
    creditEntities: PropTypes.object,
    loadCredits: PropTypes.func,
    onInvite: PropTypes.func,
    pitch: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    portfolio: PropTypes.object,
    projectEntities: PropTypes.object,
    user: PropTypes.object,
    shortlist: PropTypes.func,
    isShortlisted: PropTypes.func,
    unshortlist: PropTypes.func,
    customStylesheet: PropTypes.object,
    isLoadingOwnBriefs: PropTypes.bool,
  };

  static defaultProps = {
    collaborations: [],
    user: null,
    pitch: false,
    customStylesheet: styles,
  };

  constructor(props) {
    super(props);

    this.state = {
      skillString: this.getSkillString(),
      projects: [],
      loadedCredits: false,
    };
  }

  componentDidMount() {
    if (this.props.user) {
      this._loadCredits();

      if (
        this.props.authUser &&
        !get(this.props.portfolio, [
          this.props.user.username,
          'isShortlisted',
        ]) &&
        !get(
          this.props.portfolio,
          [this.props.user.username, 'loadingShortlistState'],
          false
        )
      ) {
        this.props.isShortlisted(this.props.user.username);
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  componentDidUpdate({ user: prevUser }) {
    if (!isEqual(prevUser, this.props.user)) {
      this.setSkillSting();
      this.reloadCredits();
    }
    if (this.getProjectEntities().length !== this.state.projects.length) {
      this.setState({ projects: this.getProjectEntities() });
    }
  }

  getProjectEntities = () => {
    const { creditEntities, portfolio, projectEntities, user } = this.props;

    if (user === null) {
      return [];
    }

    return get(portfolio, [user.username, 'credits'], [])
      .map((creditId) => get(creditEntities, [creditId], {}))
      .map(({ project_id: projectId }) =>
        get(projectEntities, [projectId], null)
      )
      .filter(Boolean);
  };

  getSkillString = () => {
    // const {
    //   user: { skills },
    // } = this.props;

    const skills = this.props.user ? this.props.user.skills : [];

    if (!skills || skills.length === 0) {
      return '';
    }

    const { beginner, intermediate, expert } = skillLevels;

    const skillFilter =
      (level) =>
      ({ experience }) =>
        level === 'expert'
          ? experience >= expert
          : level === 'intermediate'
          ? experience >= intermediate && experience < expert
          : level === 'beginner'
          ? experience <= beginner && experience > 0
          : experience === 0;

    const skillReducer =
      (omitCount = false) =>
      (string, { name }, index, array) => {
        const maxSkills = 3;
        const shouldInclude =
          array.length <= maxSkills || index <= maxSkills - 1;
        const endOfSentence =
          index === maxSkills - 1 || index === array.length - 1;
        const remainingSkillsCount = array.length - 1 - index;

        return shouldInclude
          ? `${string}${name}${endOfSentence ? '' : ', '} ${
              endOfSentence && remainingSkillsCount > 0
                ? `+${omitCount ? '' : ` ${remainingSkillsCount}`} more`
                : ''
            }`
          : string;
      };

    const isFreelancerLevel = (level) => skills.some(skillFilter(level));

    const getString = (level, startString, omitCount) =>
      skills
        .filter(skillFilter(level))
        .reduce(skillReducer(omitCount), startString);

    return isFreelancerLevel('expert')
      ? getString('expert', 'Expert in: ')
      : isFreelancerLevel('intermediate')
      ? getString('intermediate', 'Intermediate in: ')
      : isFreelancerLevel('beginner')
      ? getString('beginner', 'Beginner at: ')
      : getString(null, 'Skilled in: ', true);
  };

  setSkillSting = () => this.setState({ skillString: this.getSkillString() });

  getShortlistState = () => {
    if (this.props.authUser && this.props.user) {
      const { username } = this.props.user;
      const shortlistState = get(
        this.props.portfolio,
        [username, 'isShortlisted'],
        false
      );
      return shortlistState;
    }

    return false;
  };

  _loadCredits = () => {
    const {
      loadCredits,
      portfolio,
      user: { top_roles: roles, username },
    } = this.props;

    const [firstRole] = roles;

    if (
      isCreativeJob(firstRole) &&
      !get(portfolio, [username, 'loadingCredits'], false) &&
      !this.state.loadedCredits
    ) {
      const callback = () =>
        this.setState({
          projects: this.getProjectEntities(),
          loadedCredits: true,
        });

      if (this.getProjectEntities().length === 0) {
        loadCredits(username).then(callback);
      } else {
        callback();
      }
    }
  };

  reloadCredits = () =>
    this.setState({ loadedCredits: false }, this._loadCredits);

  handleShortlist = (trackEventData = null) => {
    const { username } = this.props.user;

    if (!this.props.authUser) {
      document.location = `/signin`;
    }

    if (get(this.props.portfolio, [username, 'isShortlisted'], false)) {
      this.props.unshortlist(username);
    } else {
      trackEvent('user-shortlists-freelancer', {
        shortlist_type: trackEventData,
      });
      this.props.shortlist(username);
    }
  };

  render() {
    const {
      collaborations,
      onInvite,
      pitch,
      user,
      customStylesheet,
      preferredRole,
      isLoadingOwnBriefs,
    } = this.props;

    const { projects } = this.state;
    const [firstRole] = user ? user.top_roles : [''];
    const styles = customStylesheet;

    const role = preferredRole ?? firstRole;

    const shouldDisplayCarousel =
      isCreativeJob(role) &&
      !isMusicJob(role) &&
      projects.filter((project) => /image|video|other/.test(project?.mediaType))
        .length > 0;
    const shouldDisplayAudioThumbnails =
      isMusicJob(role) &&
      projects.filter((project) => /audio|other/.test(project?.mediaType))
        .length > 0;

    const roles = user ? (
      <Link
        className="no-decoration"
        to={user.links.main_relative}
        title={user.displayname}
        target="_blank"
      >
        {user.top_roles.length <= 0 ? (
          <span>No roles</span>
        ) : (
          <span>
            {user.top_roles.slice(0, 3).map((role, key) => (
              <span
                key={`usercard2-toproles-${user.id}-${key}`}
                className={styles.role}
              >
                {role}
              </span>
            ))}
          </span>
        )}
      </Link>
    ) : (
      <div className="no-decoration">No roles</div>
    );

    const contactButton = isLoadingOwnBriefs ? (
      <ButtonWithLoader
        bsStyle="primary"
        style={{
          display: 'grid',
          width: '100%',
          borderWidth: '2px',
          fontWeight: 700,
          margin: 0,
        }}
        loading={true}
        text="Contact"
        onClick={() => {}}
        className="btn"
        disabled={true}
      />
    ) : collaborations.length > 0 &&
      typeof collaborations[0] === 'object' &&
      collaborations[0].hasOwnProperty('id') ? (
      collaborations.length === 1 ? (
        <Button
          bsStyle="primary"
          onClick={() => onInvite(collaborations[0], user)}
          block
          style={{ borderWidth: '2px', fontWeight: 700 }}
        >
          Contact
        </Button>
      ) : (
        <Dropdown
          style={{ display: 'grid', borderWidth: '2px', fontWeight: 700 }}
          id="hireme"
          onClick={(event) => event.stopPropagation()}
        >
          <Dropdown.Toggle
            style={{ borderWidth: '2px', fontWeight: 700 }}
            bsStyle="primary"
          >
            Contact
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <LinkContainer to={`/${user.username}/hire`}>
              <MenuItem eventKey="1">Contact for new project</MenuItem>
            </LinkContainer>
            <MenuItem divider />
            <MenuItem header eventKey="2">
              Request application to:
            </MenuItem>
            {collaborations.map((brief, key) => (
              <MenuItem
                key={`usercard2-invitebutton-menuitem-${brief.id}`}
                eventKey={key}
              >
                <div
                  className={styles.collaborationDropdownItem}
                  onClick={() => onInvite(brief, user)}
                  key={`usercard2-invitebutton-textdiv-${brief.id}`}
                >
                  {brief.text}
                </div>
              </MenuItem>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      )
    ) : (
      // Hire Button
      <span>
        {user && (
          <LinkContainer to={`/${user.username}/hire`}>
            <Button
              bsStyle="primary"
              block
              className={styles.boldButton}
              style={{
                borderWidth: '2px',
              }}
            >
              Contact
            </Button>
          </LinkContainer>
        )}
      </span>
    );

    return (
      <Fragment>
        <div className={styles.profilePhoto}>
          {user ? (
            <Link
              className="no-decoration"
              to={user.links.main_relative}
              title={user.displayname}
              target="_blank"
            >
              <div key={`usercard2_avatar_${user.id}`}>
                <Thumbnail
                  thumbnails={user.avatars}
                  className={styles.profilePictureContainer}
                  circle
                />
              </div>
            </Link>
          ) : (
            <div className={styles.profilePicturePlaceholder} />
          )}
        </div>

        {user ? (
          user.top_roles.length > 0 && (
            <div className={`${styles.roles} visible-sm visible-md visible-lg`}>
              {roles}
            </div>
          )
        ) : (
          <div className={`${styles.roles} vidible-sm visible-md visible-lg`}>
            empty user Roles
          </div>
        )}

        <div
          className={`${styles.shortlistBtn} pointer`}
          onClick={() => this.handleShortlist(!pitch ? 'browse' : 'pitch')}
        >
          <SvgIcon
            icon="heart"
            size="18"
            color={this.getShortlistState() ? '#D9534F' : '#D3D3D3'}
          />
        </div>

        <div className={styles.nameInfo}>
          <div className={styles.displayname}>
            <div className={styles.nameContainer}>
              {user ? (
                <Link
                  className="no-decoration"
                  to={user.links.main_relative}
                  title={user.displayname}
                  target="_blank"
                >
                  {user.displayname}
                  <Verified
                    user={user}
                    style={{
                      position: 'relative',
                      top: '-1px',
                      marginLeft: '5px',
                    }}
                  />
                  <Pro
                    user={user}
                    style={{
                      display: 'inline',
                      fontSize: '10px',
                      position: 'relative',
                      top: '-1px',
                      marginLeft: '3px',
                    }}
                  />
                </Link>
              ) : (
                <div className="no-decoration">user displayname</div>
              )}
              {user && (
                <div className={styles.lastSeen}>
                  <LastSeen user={user} />
                </div>
              )}
              {user && (
                <div className={styles.profileRating}>
                  <Link
                    className="no-decoration"
                    to={user.links.main_relative}
                    title={user.displayname}
                    target="_blank"
                  >
                    <Rating
                      rating={calculateUserRating(user)}
                      style={{
                        cursor: 'pointer',
                        display: 'inline-block',
                        marginRight: '5px',
                      }}
                    />
                    {user.rating.count > 0 && (
                      <span className="pointer">{` (${user.rating.count})`}</span>
                    )}
                  </Link>
                </div>
              )}
            </div>
          </div>
          <div className={styles.location}>
            {user && (
              <Link
                className="no-decoration"
                to={user.links.main_relative}
                title={user.displayname}
                target="_blank"
              >
                <Icon icon="&#xe694;" />{' '}
                {user.location.text !== '' ? user.location.text : 'Unknown'}
              </Link>
            )}
          </div>
          <div className={`${styles.roles} visible-xs`}>{roles}</div>
        </div>
        {user && <div className={styles.dayRate}></div>}
        <div className={styles.contact}>
          {!pitch && <div className={styles.contactBtn}>{contactButton}</div>}
          {!pitch && (
            <div className={`${styles.moreInfo} visible-xs`}>
              {user && (
                <ButtonOutlined
                  to={user.links.main_relative}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  More info <SvgIcon icon="chevron-right" size="14" />
                </ButtonOutlined>
              )}
            </div>
          )}
        </div>
        {user && user.skills && user.skills.length > 0 && (
          <div className={styles.skills}>{this.state.skillString}</div>
        )}
        {pitch && <hr className={styles.pitchDivider} />}
        {pitch && pitch.text && (
          <div className={styles.pitch}>
            <Markdown text={pitch.text} target="_blank" />
          </div>
        )}
        {!shouldDisplayCarousel && (
          <div
            className={`${styles.bio} ${
              shouldDisplayAudioThumbnails ? styles.audioBio : ''
            }`}
          >
            {user ? (
              <div className={styles.userBioContainer}>
                {user.bio.length > 0 ? (
                  <Shiitake renderFullOnServer={false} lines={4}>
                    {user.bio}
                  </Shiitake>
                ) : (
                  <em>No Bio</em>
                )}
                {!pitch && (
                  <Link
                    className="no-decoration"
                    to={user.links.main_relative}
                    title={user.displayname}
                    target="_blank"
                  >
                    <span
                      className="visible-sm visible-md visible-lg"
                      style={{
                        display: 'flex !important',
                        alignItems: 'center',
                        color: '#40A0F5',
                      }}
                    >
                      More info <SvgIcon icon="chevron-right" size="15" />
                    </span>
                  </Link>
                )}
              </div>
            ) : (
              <div className={styles.bio}>
                {' '}
                vulputate rhoncus. Mauris In vitae accumsan quam. Pellentesque
                convallis nisl vitae nisl tincidunt, ut aliquam velit bibendum.
                Nullam eget sagittis est. Vivamus sit amet finibus nunc. Nam eu
                mauris commodo, tempor tortor id, rutrum purus. Praesent
                condimentum ullamcorper fringilla. Aliquam erat volutpat.
              </div>
            )}
          </div>
        )}
        {shouldDisplayCarousel && (
          <div className={styles.projectCarousel}>
            <ProjectCarousel user={user} projects={projects} />
          </div>
        )}
        {shouldDisplayAudioThumbnails && (
          <div className={styles.contentBox}>
            <Link
              className="no-decoration"
              to={user.links.main_relative}
              target="_blank"
            >
              <AudioThumbnails projects={projects} />
            </Link>
          </div>
        )}
      </Fragment>
    );
  }
}
